<template>
  <div class="tracklist">
    <PageHeading title="Tracklist" />
    <section class="tracklist__collection">
      <div class="tracklist__controller">
        <SliderController :slider="slider" :current-slide="currentSlide" :displayed-days="displayedDays" />
      </div>
      <div ref="sliderContainer" class="tracklist__collection-inner">
        <div v-for="day in days" :key="day.date" class="tracklist__day">
          <h2 class="tracklist__day-heading">
            {{ day.dateString }}
          </h2>
          <TracklistTracks v-if="day.tracks && day.tracks.length" :tracks="day.tracks" />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { Ref } from 'vue'
import { format, subDays, compareAsc } from 'date-fns'

const today = Date.now()
const daysAmount = 5
const days = ref([])

const displayedDays: Ref<number> = ref(1)

for (let i = 0; i < daysAmount; i++) {
  const date = subDays(today, i)
  const dateQuery = format(date, 'yyyyMMdd')
  const { data } = useFetch('/api/tracklist', {
    query: { date: dateQuery }
  })
  days.value.push({
    date,
    dateString: useFormatDate('fullDateWeekday', date),
    tracks: data
  })
}

days.value.sort((a, b) => {
  return compareAsc(a.date, b.date)
})

// initiate slider
const currentSlide = ref(days.value.length)
const sliderOptions = {
  loop: false,
  drag: true,
  selector: '.tracklist__day',
  initial: days.value.length,
  slides: {
    perView: displayedDays.value,
    spacing: 30
  }
}

const [sliderContainer, slider] = useKeenSlider({
  ...sliderOptions,
  slideChanged: (s) => {
    currentSlide.value = s.track.details.rel
  }
})

useCDNHeaders((helper) => {
  helper
    .public()
    .setNumeric('maxAge', 5 * 60)
    .set('staleIfError', 5 * 60)
    .set('staleWhileRevalidate', 5 * 60)
    .set('sharedMaxAge', 5 * 60)
})

useSeoMeta({
  title: 'Tracklist'
})

</script>

<style lang="scss">
@use "~/assets/sass/tools";

.tracklist {
  &__controller {
    @include tools.page-grid-columns;
  }

  &__collection {
    @include tools.grid;
  }

  &__collection-inner {
    @include tools.page-grid-columns;
    display: flex;
    overflow: hidden;
  }

  &__day-heading {
    margin-top: 0;
    margin-bottom: var(--grid-gutter--s);
  }

  &__day {
    display: flex;
    flex-direction: column;
    min-width: 100%;
    width: 100%;
  }
}
</style>
